import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import { OPERATION_TERMINAL, type Operation } from "./types";

const INTERVAL_MS = 1500;

export function useOperationPoll(operationId: string | undefined) {
  const [operation, setOperation] = useState<Operation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(operationId));
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!operationId) {
      setOperation(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const tick = async () => {
      try {
        const op = await api.operation(operationId, controller.signal);
        if (!alive.current) return;
        setOperation(op);
        setLoading(false);
        if (!OPERATION_TERMINAL.has(op.state)) {
          window.setTimeout(() => {
            if (alive.current) void tick();
          }, INTERVAL_MS);
        }
      } catch (e) {
        if (!alive.current || controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Could not refresh operation");
        setLoading(false);
        window.setTimeout(() => {
          if (alive.current) void tick();
        }, INTERVAL_MS * 2);
      }
    };

    void tick();

    return () => {
      alive.current = false;
      controller.abort();
    };
  }, [operationId]);

  return { operation, error, loading };
}
